import React, { useEffect, useMemo, useRef, useState } from "react";
import Loading from "../../../../common-components/Loading";
import FullscreenEcharts from "./FullscreenEcharts";
import { getOverviewSectorAPI } from "../../../../api/reports-api";
import { formatActualsThroughDate, headerActualsDate } from "./plTimeFilterUtils";
import { toReportApiFilters, stripFilterSentinels } from "../filterRelationshipUtils";
import {
  usePlByProgramStyles,
  PlFilterGroup,
  PlMultiSelect,
  PlKpiRow,
  PlPageHeader,
  PL_INK as INK,
  PL_MUTED as MUTED,
  PL_PY_C as PY_C,
  PL_CY_C as CY_C,
  PL_BUD_C as BUD_C,
  PL_FONT as FONT_BODY,
  millionsYAxisTicks,
  millionsChartLabel,
  millionsBarSeriesData,
  formatMillionsLabel,
  ChartTitleBuPlural,
  PlSectionInfoTip,
} from "./plByProgramTheme";
import { useReportFonts } from "../../reportFonts";

const TIMELINE_OPTS = ["Q1", "Q2", "Q3", "Q4"];
const METRIC_OPTS = ["Vs. Prior Year", "Vs. Prior Forecast"];

const TOTAL_COMPANY_FILTER_HELP =
  "Total Company KPIs and chart always show company-wide totals. BU filters on this page only affect the Business Unit KPI row and charts.";

/* ── Month / quarter helpers ──────────────────────────────────── */
const MONTH_INDEX = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};
const QUARTER_MONTHS = {
  Q1: ["jan", "feb", "mar"],
  Q2: ["apr", "may", "jun"],
  Q3: ["jul", "aug", "sep"],
  Q4: ["oct", "nov", "dec"],
};
const ALL_MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];
const ALL_QS = ["Q1", "Q2", "Q3", "Q4"];

const FULL_MONTH_NAMES = {
  january: "jan",
  february: "feb",
  march: "mar",
  april: "apr",
  may: "may",
  june: "jun",
  july: "jul",
  august: "aug",
  september: "sep",
  october: "oct",
  november: "nov",
  december: "dec",
};

function monthNameToKey(name) {
  if (!name) return null;
  const raw = String(name).trim();
  const lower = raw.toLowerCase();
  if (MONTH_INDEX[lower]) return lower;
  const abbr = lower.slice(0, 3);
  if (MONTH_INDEX[abbr]) return abbr;
  return FULL_MONTH_NAMES[lower] || null;
}

function normalizeQuarter(q) {
  const s = String(q).trim().toUpperCase();
  if (!s) return null;
  return s.startsWith("Q") ? s : `Q${s}`;
}

function buildApiFilters(filters) {
  if (!filters) return {};
  const { monthName, quarter, ...rest } = filters;
  return toReportApiFilters(rest);
}

function deriveTimeWindowFromFilters(filters) {
  const quarters = stripFilterSentinels(filters?.quarter)
    .map(normalizeQuarter)
    .filter((q) => ALL_QS.includes(q));
  const monthKeys = stripFilterSentinels(filters?.monthName)
    .map(monthNameToKey)
    .filter(Boolean);

  let timeline = [];
  if (quarters.length) {
    timeline = quarters;
  } else if (monthKeys.length) {
    const qs = new Set();
    monthKeys.forEach((m) => {
      Object.entries(QUARTER_MONTHS).forEach(([q, months]) => {
        if (months.includes(m)) qs.add(q);
      });
    });
    timeline = ALL_QS.filter((q) => qs.has(q));
  }

  let effectiveMonthKeys = null;
  if (monthKeys.length) {
    const allowed = quarters.length ? getWindowMonths(quarters) : ALL_MONTHS;
    const narrowed = monthKeys.filter((m) => allowed.includes(m));
    effectiveMonthKeys = narrowed.length ? narrowed : monthKeys;
  }

  return { timeline, monthKeys: effectiveMonthKeys };
}

function getWindowMonths(timelines, monthKeys) {
  if (monthKeys?.length) return monthKeys;
  if (!timelines || timelines.length === 0) return ALL_MONTHS;
  const months = [];
  timelines.forEach((q) => {
    (QUARTER_MONTHS[q] || []).forEach((m) => {
      if (!months.includes(m)) months.push(m);
    });
  });
  return months.length ? months : ALL_MONTHS;
}

function getWindowTotal(ds, timelines, monthKeys) {
  if (!ds) return 0;
  if (monthKeys?.length) return sumMonths(ds, monthKeys);
  if (!timelines || timelines.length === 0) return ds.total || 0;
  return timelines.reduce((acc, q) => acc + (ds[q.toLowerCase()] || 0), 0);
}

function sumMonths(ds, months) {
  if (!ds) return 0;
  return months.reduce((acc, m) => acc + (ds[m] || 0), 0);
}

function varPct(main, compare) {
  return main !== 0 ? ((main - compare) / Math.abs(main)) * 100 : 0;
}

const fmtM = (v) => (v / 1_000_000).toFixed(1);

/* Sum byBu entries for selected BUs → {cy, py, pf, bud} rowData */
function sumByBu(byBu, selectedBus) {
  const versions = { cy: {}, py: {}, pf: {}, bud: {} };
  const buKeys = selectedBus?.length ? selectedBus : Object.keys(byBu || {});
  buKeys.forEach((b) => {
    const cell = (byBu || {})[b] || {};
    ["cy", "py", "pf", "bud"].forEach((v) => {
      Object.entries(cell[v] || {}).forEach(([col, val]) => {
        versions[v][col] = (versions[v][col] || 0) + (val || 0);
      });
    });
  });
  return versions;
}

/* Build all-BU chart items from the invariant `bus` dataset, sorted by Budget desc */
function formatBuAxisLabel(name) {
  const map = {
    ARMY: "Army",
    "CITIZEN SERVICES": "Citizen Services",
    "Citizen Services": "Citizen Services",
    "CCMD/4th Estate": "CCMD/4th Estate",
    "CCMD/4E": "CCMD/4E",
    Navy: "Navy",
    NAVY: "Navy",
    Corporate: "Corporate",
    Commercial: "Commercial",
    "Agile Labs": "Agile Labs",
  };
  return wrapAxisLabel(map[name] || name, 10);
}

function wrapAxisLabel(name, maxLine = 10) {
  const s = String(name || "").trim();
  if (!s || s.length <= maxLine) return s;

  const space = s.lastIndexOf(" ", maxLine);
  if (space >= 3) return `${s.slice(0, space)}\n${s.slice(space + 1)}`;

  const slash = s.indexOf("/");
  if (slash > 0 && slash < s.length - 1) {
    return `${s.slice(0, slash + 1)}\n${s.slice(slash + 1)}`;
  }

  return `${s.slice(0, maxLine)}\n${s.slice(maxLine)}`;
}

function buildAllBuItems(bus, businessUnits, timelines, monthKeys) {
  if (!bus || !businessUnits?.length) return [];
  const items = businessUnits.map((buName) => {
    const rowData = bus[buName] || {};
    return {
      name: buName,
      py: parseFloat(fmtM(getWindowTotal(rowData.py, timelines, monthKeys))),
      cy: parseFloat(fmtM(getWindowTotal(rowData.cy, timelines, monthKeys))),
      bud: parseFloat(fmtM(getWindowTotal(rowData.bud, timelines, monthKeys))),
    };
  });
  /* Suppress all-zero BUs; sort by Budget descending */
  return items
    .filter((d) => d.cy !== 0 || d.py !== 0 || d.bud !== 0)
    .sort((a, b) => b.bud - a.bud);
}

/* ─────────────────────────────────────────────────────────────── */
/* Chart builders                                                  */
/* ─────────────────────────────────────────────────────────────── */

function FullscreenTitleIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

function deriveKpis(rowData, timelines, cutoff, metricChoice, monthKeys) {
  if (!rowData?.cy)
    return {
      cyForecast: "0.0",
      ytdActuals: "0.0",
      etc: "0.0",
      budget: "0.0",
      blForecast: "0.0",
      blYtd: "0.0",
      blEtc: "0.0",
    };
  const wm = getWindowMonths(timelines, monthKeys);
  const ytdM = wm.filter((m) => MONTH_INDEX[m] <= cutoff);
  const etcM = wm.filter((m) => MONTH_INDEX[m] > cutoff);
  const bl = metricChoice === "Vs. Prior Forecast" ? rowData.pf : rowData.py;
  return {
    cyForecast: fmtM(getWindowTotal(rowData.cy, timelines, monthKeys)),
    ytdActuals: fmtM(sumMonths(rowData.cy, ytdM)),
    etc: fmtM(sumMonths(rowData.cy, etcM)),
    budget: fmtM(getWindowTotal(rowData.bud, timelines, monthKeys)),
    blForecast: fmtM(getWindowTotal(bl, timelines, monthKeys)),
    blYtd: fmtM(sumMonths(bl, ytdM)),
    blEtc: fmtM(sumMonths(bl, etcM)),
  };
}

function deriveVarData(rowData, timelines, cutoff, metricChoice, monthKeys) {
  if (!rowData?.cy) return {};
  const wm = getWindowMonths(timelines, monthKeys);
  const ytdM = wm.filter((m) => MONTH_INDEX[m] <= cutoff);
  const etcM = wm.filter((m) => MONTH_INDEX[m] > cutoff);
  const cy = getWindowTotal(rowData.cy, timelines, monthKeys);
  const ytd = sumMonths(rowData.cy, ytdM);
  const etc = sumMonths(rowData.cy, etcM);
  const bl = metricChoice === "Vs. Prior Forecast" ? rowData.pf : rowData.py;
  const bxT = getWindowTotal(bl, timelines, monthKeys);
  const bxY = sumMonths(bl, ytdM);
  const bxE = sumMonths(bl, etcM);
  const buT = getWindowTotal(rowData.bud, timelines, monthKeys);
  const buY = sumMonths(rowData.bud, ytdM);
  const buE = sumMonths(rowData.bud, etcM);
  return {
    cyVsPY: { pct: varPct(cy, bxT), diff: cy - bxT },
    cyVsBud: { pct: varPct(cy, buT), diff: cy - buT },
    ytdVsPY: { pct: varPct(ytd, bxY), diff: ytd - bxY },
    ytdVsBud: { pct: varPct(ytd, buY), diff: ytd - buY },
    etcVsPY: { pct: varPct(etc, bxE), diff: etc - bxE },
    etcVsBud: { pct: varPct(etc, buE), diff: etc - buE },
  };
}

function rowDataToChartData(rowData, timelines, monthKeys) {
  if (!rowData?.cy)
    return {
      quarters: ALL_QS,
      py: [0, 0, 0, 0],
      cy: [0, 0, 0, 0],
      budget: [0, 0, 0, 0],
    };
  if (monthKeys?.length) {
    return {
      quarters: monthKeys.map((m) => m[0].toUpperCase() + m.slice(1)),
      py: monthKeys.map((m) => parseFloat(fmtM(rowData.py?.[m] || 0))),
      cy: monthKeys.map((m) => parseFloat(fmtM(rowData.cy?.[m] || 0))),
      budget: monthKeys.map((m) => parseFloat(fmtM(rowData.bud?.[m] || 0))),
    };
  }
  const isAll = !timelines || timelines.length === 0;
  if (isAll || timelines.length > 1) {
    const qs = isAll ? ALL_QS : ALL_QS.filter((q) => timelines.includes(q));
    return {
      quarters: qs,
      py: qs.map((q) => parseFloat(fmtM(rowData.py?.[q.toLowerCase()] || 0))),
      cy: qs.map((q) => parseFloat(fmtM(rowData.cy?.[q.toLowerCase()] || 0))),
      budget: qs.map((q) =>
        parseFloat(fmtM(rowData.bud?.[q.toLowerCase()] || 0)),
      ),
    };
  }
  const months = QUARTER_MONTHS[timelines[0]] || [];
  return {
    quarters: months.map((m) => m[0].toUpperCase() + m.slice(1)),
    py: months.map((m) => parseFloat(fmtM(rowData.py?.[m] || 0))),
    cy: months.map((m) => parseFloat(fmtM(rowData.cy?.[m] || 0))),
    budget: months.map((m) => parseFloat(fmtM(rowData.bud?.[m] || 0))),
  };
}

function buildQuarterlyOption(data, showPY) {
  const pyVals = showPY ? data.py : [];
  const maxAbs = Math.max(
    0,
    ...[...pyVals, ...data.cy, ...data.budget]
      .map((v) => Math.abs(Number(v)))
      .filter(Number.isFinite),
  );
  const barOpts = { maxAbs };
  const lineLabel = millionsChartLabel({
    position: "top",
    distance: 8,
    rotate: 0,
    align: "center",
    verticalAlign: "bottom",
    color: BUD_C,
    backgroundColor: "rgba(255,255,255,0.92)",
    borderRadius: 2,
    padding: [1, 2],
  });
  const series = [];
  if (showPY)
    series.push({
      name: "PY",
      type: "bar",
      data: millionsBarSeriesData(data.py, PY_C, barOpts),
      barMaxWidth: 24,
      itemStyle: { color: PY_C, borderRadius: [3, 3, 0, 0] },
    });
  series.push({
    name: "CY Forecast",
    type: "bar",
    data: millionsBarSeriesData(data.cy, CY_C, barOpts),
    barMaxWidth: 24,
    itemStyle: { color: CY_C, borderRadius: [3, 3, 0, 0] },
  });
  series.push({
    name: "Budget",
    type: "line",
    data: data.budget,
    smooth: true,
    symbol: "circle",
    symbolSize: 6,
    lineStyle: { color: BUD_C, width: 2.5 },
    itemStyle: { color: BUD_C, borderColor: "#fff", borderWidth: 2 },
    label: lineLabel,
  });
  const yAxisTicks = millionsYAxisTicks(pyVals, data.cy, data.budget);
  return {
    grid: { top: 52, right: 8, bottom: 28, left: 8, containLabel: true },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: "#fff",
      borderColor: "#E2E8F0",
      borderWidth: 1,
      textStyle: {
        fontSize: 11,
        color: INK,
        fontFamily: FONT_BODY,
        fontWeight: 600,
      },
      formatter: (params) => {
        let s = `<b style="font-size:11px">${params[0].axisValue}</b><br/>`;
        params.forEach((p) => {
          s += `${p.marker} ${p.seriesName}: <b>${formatMillionsLabel(p.value)}</b><br/>`;
        });
        return s;
      },
    },
    legend: {
      top: 4,
      icon: "circle",
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 14,
      textStyle: {
        fontSize: 11,
        color: MUTED,
        fontFamily: FONT_BODY,
        fontWeight: 700,
      },
    },
    xAxis: {
      type: "category",
      data: data.quarters,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        fontSize: 11,
        color: MUTED,
        fontWeight: 700,
        fontFamily: FONT_BODY,
      },
    },
    yAxis: {
      type: "value",
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: "#F1F5F9", type: "dashed" } },
      ...yAxisTicks,
      axisLabel: {
        fontSize: 10,
        color: MUTED,
        fontFamily: FONT_BODY,
        fontWeight: 700,
        ...yAxisTicks.axisLabel,
      },
    },
    series,
  };
}

function buildAllBuOption(data, showPY) {
  const names = data.map((d) => d.name);
  const dense = names.length > 8;
  const pyVals = showPY ? data.map((d) => d.py) : [];
  const cyVals = data.map((d) => d.cy);
  const budVals = data.map((d) => d.bud);
  const maxAbs = Math.max(
    0,
    ...[...pyVals, ...cyVals, ...budVals]
      .map((v) => Math.abs(Number(v)))
      .filter(Number.isFinite),
  );
  const barOpts = {
    maxAbs,
    fontSize: dense ? 8 : 9,
  };
  const lineLabel = millionsChartLabel({
    fontSize: dense ? 8 : 9,
    position: "top",
    distance: 8,
    rotate: 0,
    align: "center",
    verticalAlign: "bottom",
    color: BUD_C,
    backgroundColor: "rgba(255,255,255,0.92)",
    borderRadius: 2,
    padding: [1, 2],
  });

  const series = [];
  if (showPY)
    series.push({
      name: "PY",
      type: "bar",
      data: millionsBarSeriesData(pyVals, PY_C, barOpts),
      barMaxWidth: 16,
      itemStyle: { color: PY_C, borderRadius: [3, 3, 0, 0] },
    });
  series.push({
    name: "CY Forecast",
    type: "bar",
    data: millionsBarSeriesData(cyVals, CY_C, barOpts),
    barMaxWidth: 16,
    itemStyle: { color: CY_C, borderRadius: [3, 3, 0, 0] },
  });
  series.push({
    name: "Budget",
    type: "line",
    data: budVals,
    smooth: true,
    symbol: "circle",
    symbolSize: 5,
    lineStyle: { color: BUD_C, width: 2.2 },
    itemStyle: { color: BUD_C, borderColor: "#fff", borderWidth: 2 },
    label: lineLabel,
  });
  const yAxisTicks = millionsYAxisTicks(pyVals, cyVals, budVals);
  return {
    grid: { top: 52, right: 8, bottom: 44, left: 8, containLabel: true },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: "#fff",
      borderColor: "#E2E8F0",
      borderWidth: 1,
      textStyle: {
        fontSize: 11,
        color: INK,
        fontFamily: FONT_BODY,
        fontWeight: 600,
      },
      formatter: (params) => {
        let s = `<b style="font-size:11px">${params[0].axisValue}</b><br/>`;
        params.forEach((p) => {
          s += `${p.marker} ${p.seriesName}: <b>${formatMillionsLabel(p.value)}</b><br/>`;
        });
        return s;
      },
    },
    legend: {
      top: 4,
      icon: "circle",
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 14,
      textStyle: {
        fontSize: 11,
        color: MUTED,
        fontFamily: FONT_BODY,
        fontWeight: 700,
      },
    },
    xAxis: {
      type: "category",
      data: names,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        fontSize: 10,
        color: MUTED,
        fontWeight: 700,
        rotate: 0,
        fontFamily: FONT_BODY,
        interval: 0,
        margin: 8,
        hideOverlap: true,
        lineHeight: 14,
        formatter: (v) => formatBuAxisLabel(v),
      },
    },
    yAxis: {
      type: "value",
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: "#F1F5F9", type: "dashed" } },
      ...yAxisTicks,
      axisLabel: {
        fontSize: 10,
        color: MUTED,
        fontFamily: FONT_BODY,
        fontWeight: 700,
        ...yAxisTicks.axisLabel,
      },
    },
    series,
  };
}

/* ─────────────────────────────────────────────────────────────── */
/* MetricBlock                                                     */
/* onTimeline set  → Revenue  (timeline + compare + BU dropdowns) */
/* else            → Gross Margin (BU dropdown; timeline/compare read-only) */
/* ─────────────────────────────────────────────────────────────── */
function MetricBlock({
  metricLabel,
  metricCode,
  companyRowData,
  buRowData,
  allBuItems,
  cutoff,
  timeline,
  monthKeys,
  metric,
  bu,
  onTimeline,
  onMetric,
  onBu,
  buOpts,
  classes,
  sectionCardClassName,
}) {
  const showTimelineFilters = !!onTimeline;
  const showPY = metric === "Vs. Prior Year";
  const companyChartRef = useRef(null);
  const allBuChartRef = useRef(null);
  const buChartRef = useRef(null);

  const companyKpi = deriveKpis(
    companyRowData,
    timeline,
    cutoff,
    metric,
    monthKeys,
  );
  const companyVar = deriveVarData(
    companyRowData,
    timeline,
    cutoff,
    metric,
    monthKeys,
  );
  const buKpi = deriveKpis(buRowData, timeline, cutoff, metric, monthKeys);
  const buVar = deriveVarData(buRowData, timeline, cutoff, metric, monthKeys);
  const companyQData = rowDataToChartData(companyRowData, timeline, monthKeys);
  const buQData = rowDataToChartData(buRowData, timeline, monthKeys);

  const buTitle =
    bu.length === 0 ? "All BUs" : bu.length === 1 ? bu[0] : `${bu.length} BUs`;

  return (
    <div className={`${classes.sectionCard} ${sectionCardClassName || ""}`}>
      <div className={classes.sectionToolbar}>
        <div className={classes.sectionTitleWrap}>
          <div className={classes.sectionAccent} />
          <div>
            <div className={classes.sectionTitleRow}>
              <div className={classes.sectionTitle}>{metricLabel}</div>
              {metricCode && (
                <span className={classes.sectionMetricBadge}>{metricCode}</span>
              )}
              <PlSectionInfoTip
                label="How Page Filters Affect Total Company"
                text={TOTAL_COMPANY_FILTER_HELP}
                classes={classes}
              />
            </div>
            <div className={classes.sectionSubtitle}>
              BU-level Performance &amp; Variance
            </div>
          </div>
        </div>

        <div className={classes.toolbarFilters}>
          {showTimelineFilters ? (
            <>
              <PlMultiSelect
                label="Timeline"
                options={TIMELINE_OPTS}
                value={timeline}
                onChange={onTimeline}
                classes={classes}
              />
              <PlFilterGroup
                label="Compare"
                options={METRIC_OPTS}
                value={metric}
                onChange={onMetric}
                classes={classes}
              />
              <PlMultiSelect
                label="BU"
                options={buOpts || []}
                value={bu}
                onChange={onBu}
                classes={classes}
              />
            </>
          ) : (
            <>
              <span className={classes.filterMeta}>
                <span className={classes.filterMetaKey}>Timeline</span>
                <span className={classes.filterMetaVal}>
                  {timeline.length === 0 ? "All" : timeline.join(", ")}
                </span>
              </span>
              <span className={classes.filterMeta}>
                <span className={classes.filterMetaKey}>Compare</span>
                <span className={classes.filterMetaVal}>{metric}</span>
              </span>
              <PlMultiSelect
                label="BU"
                options={buOpts || []}
                value={bu}
                onChange={onBu}
                classes={classes}
              />
            </>
          )}
        </div>
      </div>

      {/* ── Company KPI row ── */}
      <PlKpiRow
        kpiValues={companyKpi}
        varData={companyVar}
        metricChoice={metric}
        classes={classes}
        splitBaselineCard
      />

      {/* ── BU KPI row ── */}
      <div className={classes.buFilterBar}>
        <span className={classes.buBarLabel}>Business Unit View</span>
        <span className={classes.buValueBadge}>{buTitle}</span>
      </div>

      <PlKpiRow
        kpiValues={buKpi}
        varData={buVar}
        metricChoice={metric}
        classes={classes}
        splitBaselineCard
      />

      {/* ── Three chart cards ── */}
      <div className={classes.chartsRow}>
        <div className={classes.chartPanel}>
          <div className={classes.chartPanelTitle}>
            Total Company
            <button
              type="button"
              className={classes.chartTitleFsBtn}
              onClick={() => companyChartRef.current?.toggleFullscreen?.()}
              aria-label="View Fullscreen"
              title="View Fullscreen"
            >
              <FullscreenTitleIcon />
            </button>
          </div>
          <div className={classes.chartPanelBody}>
            <FullscreenEcharts
              ref={companyChartRef}
              option={buildQuarterlyOption(companyQData, showPY)}
              height="100%"
              notMerge
              showButton={false}
            />
          </div>
        </div>
        <div className={classes.chartPanel}>
          <div className={classes.chartPanelTitle}>
            BU
            <button
              type="button"
              className={classes.chartTitleFsBtn}
              onClick={() => allBuChartRef.current?.toggleFullscreen?.()}
              aria-label="View Fullscreen"
              title="View Fullscreen"
            >
              <FullscreenTitleIcon />
            </button>
          </div>
          <div className={classes.chartPanelBody}>
            <FullscreenEcharts
              ref={allBuChartRef}
              option={buildAllBuOption(allBuItems, showPY)}
              height="100%"
              notMerge
              showButton={false}
            />
          </div>
        </div>
        <div className={classes.chartPanel}>
          <div className={classes.chartPanelTitle}>
            {bu.length === 0 ? (
              <ChartTitleBuPlural prefix="All" classes={classes} />
            ) : bu.length === 1 ? (
              bu[0]
            ) : (
              <ChartTitleBuPlural prefix={bu.length} classes={classes} />
            )}
            <button
              type="button"
              className={classes.chartTitleFsBtn}
              onClick={() => buChartRef.current?.toggleFullscreen?.()}
              aria-label="View Fullscreen"
              title="View Fullscreen"
            >
              <FullscreenTitleIcon />
            </button>
          </div>
          <div className={classes.chartPanelBody}>
            <FullscreenEcharts
              ref={buChartRef}
              option={buildQuarterlyOption(buQData, showPY)}
              height="100%"
              notMerge
              showButton={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/* Root — filter state lives here, shared by both blocks          */
/* ─────────────────────────────────────────────────────────────── */
export default function OverviewSector({ filters, actualsThroughDate }) {
  const classes = usePlByProgramStyles();
  useReportFonts();

  /* Client-only slicers — no refetch */
  const [timeline, setTimeline] = useState([]);
  const [metric, setMetric] = useState("Vs. Prior Year");

  /* Per-metric BU selections */
  const [revBu, setRevBu] = useState([]);
  const [gmBu, setGmBu] = useState([]);

  /* BU dropdown options (same list for both metrics) */
  const [buOpts, setBuOpts] = useState([]);

  /* API data */
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);

  const apiFilterKey = useMemo(
    () => JSON.stringify(buildApiFilters(filters)),
    [filters],
  );

  const globalTimeWindow = useMemo(
    () => deriveTimeWindowFromFilters(filters),
    [filters?.monthName, filters?.quarter],
  );
  const globalTimelineKey = useMemo(
    () => JSON.stringify(globalTimeWindow.timeline),
    [globalTimeWindow.timeline],
  );
  const globalMonthKeys = globalTimeWindow.monthKeys;

  useEffect(() => {
    setTimeline(globalTimeWindow.timeline);
  }, [globalTimelineKey, globalTimeWindow.timeline]);

  /* Refetch only when non-time global filters change */
  useEffect(() => {
    setLoading(true);
    setApiData(null);
    getOverviewSectorAPI(buildApiFilters(filters))
      .then((res) => {
        if (res?.data) {
          const d = res.data;
          setApiData(d);
          setBuOpts(d.businessUnits || []);
          setRevBu([]);
          setGmBu([]);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [apiFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Derive display data client-side */
  const cutoff = apiData?.actualThroughMonth || 12;
  const headerDate = headerActualsDate(
    filters?.year,
    loading,
    formatActualsThroughDate(apiData?.actualThroughMonth),
  );
  const metaStr = apiData
    ? `${apiData.cyVersion?.name || "Live EAC"} · Vs. ${apiData.priorForecastVersion?.name || "PF"} · ${apiData.budgetVersion?.name || "Budget"}`
    : "Revenue & Gross Margin · BU Overview";

  /* Revenue */
  const revByBu = apiData?.metrics?.rev?.byBu || {};
  const revBus = apiData?.metrics?.rev?.bus || {};
  const revTotalCompany = apiData?.metrics?.rev?.totalCompany || {};
  const revCompanyData = revTotalCompany;
  const revBuData = sumByBu(revByBu, revBu.length ? revBu : null);
  const revAllBuItems = buildAllBuItems(
    revBus,
    apiData?.businessUnits,
    timeline,
    globalMonthKeys,
  );

  /* Gross Margin */
  const gmByBu = apiData?.metrics?.gm?.byBu || {};
  const gmBusData = apiData?.metrics?.gm?.bus || {};
  const gmTotalCompany = apiData?.metrics?.gm?.totalCompany || {};
  const gmCompanyData = gmTotalCompany;
  const gmBuData = sumByBu(gmByBu, gmBu.length ? gmBu : null);
  const gmAllBuItems = buildAllBuItems(
    gmBusData,
    apiData?.businessUnits,
    timeline,
    globalMonthKeys,
  );

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <PlPageHeader
          classes={classes}
          eyebrow="P&L by Program"
          title="Overview : BU"
          accent="BU Drill-down"
          meta={metaStr}
          metaTop={`FY-${apiData?.fiscalYear || 2026} · BU Overview`}
          date={headerDate}
        />

        {loading ? (
          <Loading />
        ) : (
          <>
            <MetricBlock
              metricLabel="Revenue"
              metricCode="REV"
              companyRowData={revCompanyData}
              buRowData={revBuData}
              allBuItems={revAllBuItems}
              cutoff={cutoff}
              timeline={timeline}
              monthKeys={globalMonthKeys}
              metric={metric}
              bu={revBu}
              onTimeline={setTimeline}
              onMetric={setMetric}
              onBu={setRevBu}
              buOpts={buOpts}
              classes={classes}
            />
            <MetricBlock
              metricLabel="Gross Margin"
              metricCode="GM"
              companyRowData={gmCompanyData}
              buRowData={gmBuData}
              allBuItems={gmAllBuItems}
              cutoff={cutoff}
              timeline={timeline}
              monthKeys={globalMonthKeys}
              metric={metric}
              bu={gmBu}
              onBu={setGmBu}
              buOpts={buOpts}
              classes={classes}
              sectionCardClassName={classes.sectionCardDelayed}
            />
          </>
        )}
      </div>
    </div>
  );
}
